import React, {
    ComponentType,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { ErrorContext } from '../../lib/ErrorContext';
import Questions from '../../lib/Questions';
import type { FormQuestion } from '~/lib/Api.types';
import { QuestionComponentProps } from '~/lib/Questions/Questions.types';
import { QuestionHandle, QuestionRefContext } from '~/lib/QuestionRefContext';
import {
    FormPart,
    isAnswered,
    part,
    questionAttributes,
} from '~/lib/FormCssApi';

type QuestionProps<QuestionValue = string> = {
    isReadOnly?: boolean;
    question: FormQuestion;
    className?: string;
    id: FormQuestion['id'];
    value: QuestionValue;
    setValue: (value: QuestionValue) => void;
    isVisible?: boolean;
    setSettings?: (settings: any) => void;
    /**
     * Which situation this render is, for question types that care. Custom
     *  questions use it to decide whether to draw a control or an answer -
     *  see ~/lib/CustomQuestions/CustomQuestionApi
     */
    mode?: string;
    answeredAt?: string | null;
};
const Question = ({
    question,
    value,
    setValue,
    className,
    id: questionId,
    isReadOnly = false,
    isVisible = true,
    setSettings,
    mode,
    answeredAt,
}: QuestionProps) => {
    const [isChangedAfterError, setIsChangedAfterError] = useState(false);
    const { label, type, settings } = question;
    const id = questionId ? `${questionId}` : `q-${type}-${Math.random()}`;

    const { errors, errorsUpdatedAt } = useContext(ErrorContext);

    let errorMessage = '';
    if (id && errors && errors[id] && !isChangedAfterError) {
        errorMessage = errors[id];
    }

    useEffect(() => {
        if (errors && id && errors[id]) {
            setIsChangedAfterError(false);
        }
    }, [JSON.stringify(errors), errorsUpdatedAt?.toString()]);

    useEffect(() => {
        setIsChangedAfterError(true);
    }, [JSON.stringify(value)]);

    const questionSchema = Questions[type ?? ''];
    const QuestionComponent: ComponentType<QuestionComponentProps> =
        questionSchema.component;

    // Question types that opt in get registered so validation can call into
    //  them, ex. to ask the browser whether a native input is in a bad state
    const questionRefs = useContext(QuestionRefContext);
    const componentRef = useRef<QuestionHandle>(null);
    const forwardsRef = !!questionSchema.forwardsRef;

    useEffect(() => {
        if (!forwardsRef || !question.id) {
            return;
        }

        questionRefs.current[question.id] = componentRef;

        return () => {
            delete questionRefs.current[question.id];
        };
    }, [question.id, forwardsRef, questionRefs]);

    // React 18 warns when a ref lands on a component that isn't a forwardRef
    const refProp: any = forwardsRef ? { ref: componentRef } : {};

    const showsErrorMessage =
        !!errorMessage && !questionSchema.componentHandlesErrors;

    return (
        <div
            {...questionAttributes({
                questionId: question.id,
                type: type ?? undefined,
                isRequired:
                    !!settings?.required || !!questionSchema.alwaysRequired,
                hasError: !!errorMessage,
                isAnswered: isAnswered(value),
            })}
            className={`${showsErrorMessage ? 'is-error' : ''} ${className ?? ''}`}
        >
            <QuestionComponent
                {...refProp}
                isReadOnly={isReadOnly}
                label={label}
                value={value}
                setValue={setValue}
                settings={{ ...settings, isError: !!errorMessage }}
                setSettings={setSettings}
                id={id}
                /**
                 * The form question's own id, separate from `id` above:
                 *  callers pass that as anything from `answers.123` to a
                 *  generated string, so it can't be used to match a question
                 *  up with anything outside this component
                 */
                questionId={question.id}
                isVisible={isVisible}
                mode={mode}
                answeredAt={answeredAt}
            />

            {showsErrorMessage && (
                <div
                    {...part(FormPart.ERROR)}
                    className="text-sm text-red-600 ml-4 mt-1"
                >
                    {errorMessage}
                </div>
            )}
        </div>
    );
};

export default Question;
